import { DataGlueGraph, NodeInstance } from '@glueapp/graph-types';
import { Node } from '@glueapp/graph-types';
import { validateGraphContent, validateGraphStrucure, ValidationResult } from './validateGraph';
import { updatePortValues } from './rules/updaters/portValueUpdating';
import { IntegrationAccess } from '@glueapp/graph-types';
import { dictionaryFromStringTuple } from '@glueapp/graph-types';
import { updateGraphIntegrationAccess } from './rules/updaters/updateGraphIntegrationAccess';
import { cleanupInvalidConnections } from './rules/updaters/connectionCleanup';
import { GraphTypeResolver } from './GraphTypeResolver';
import { addPassThroughPorts } from './rules/updaters/addPassThroughPorts';

// Y.Doc projections (`yDocToJson`) emit `inPorts`/`outPorts` as empty arrays —
// they're derived state. Validation needs them populated, so seed each node
// instance from its template before resolution. Nodes with `computeShape`
// later override this via the resolver; nodes without keep the template
// shape verbatim.
const seedPortsFromTemplate = (graph: DataGlueGraph, nodeLibraryIDDictionary: { [key: string]: Node }): DataGlueGraph => {
    const seedInstance = (instance: NodeInstance): NodeInstance => {
        if (instance.inPorts.length > 0 || instance.outPorts.length > 0) return instance;
        const template = nodeLibraryIDDictionary[instance.nodeId];
        if (!template) return instance;
        return { ...instance, inPorts: template.inPorts, outPorts: template.outPorts };
    };
    return {
        ...graph,
        nodeInstances: graph.nodeInstances.map(seedInstance),
        functions: (graph.functions ?? []).map(fn => ({
            ...fn,
            nodeInstances: fn.nodeInstances.map(seedInstance),
        })),
    };
};

export const processGraph = (
    graph: DataGlueGraph,
    nodeLibrary: Node[],
    integrationAccesses: IntegrationAccess[],
): { graph: DataGlueGraph; validationResult: ValidationResult } => {
    const nodeLibraryIDDictionary = dictionaryFromStringTuple(nodeLibrary, node => [node.id, node]);

    const structuralValidation = validateGraphStrucure(graph);

    if (structuralValidation.globalWarnings?.length) {
        return { graph, validationResult: structuralValidation };
    }

    const seededGraph = seedPortsFromTemplate(graph, nodeLibraryIDDictionary);

    const graphTypeResolver = new GraphTypeResolver(seededGraph, nodeLibraryIDDictionary);
    const resolvedTypesGraph = graphTypeResolver.getResolvedGraph();

    // Add pass-through ports after type resolution but before other updates
    // This ensures types are fully resolved before we determine which additional ports to add
    const graphWithPassThroughPorts = addPassThroughPorts(resolvedTypesGraph);

    const updatedGraph = updatePortValues(
        updateGraphIntegrationAccess(cleanupInvalidConnections(graphWithPassThroughPorts), integrationAccesses, nodeLibraryIDDictionary),
    );

    const validationResult = validateGraphContent(updatedGraph, integrationAccesses);

    return {
        graph: updatedGraph,
        validationResult,
    };
};
